import React, {useEffect, useMemo, useState} from "react";
import {Chip, Divider} from "@mui/material";
import {useLocales} from "../../../locales";
import LoadingSegment from "../../../components/loadingSegment";
import {AnimatePresence, motion} from "framer-motion";
import {BusLayout} from "../BusSeatPicker/BusLayout";
import {Legend} from "../BusSeatPicker/Legend";
import {processBusData} from "../BusSeatPicker/utils/processBusData";
import {getIniLoggedUser} from "../../../helper/session";
import {useSelector} from "react-redux";

const SEAT_STATUS = {
    AVAILABLE: 'AVAILABLE',
    OCCUPIED: 'OCCUPIED',
    RESERVED: 'RESERVED',
    SELECTED: 'SELECTED',
    DISABLED: 'DISABLED',
};

// Dodato: opcioni callback ka parentu
export default function SeatsLayout({
                                        totalPassengers,
                                        filteredSeats,
                                        loading,
                                        onChangeSelectedSeats, // optional: (list) => void
                                        tripData
                                    }) {
    const {translate} = useLocales();
    // čuvamo listu izabranih { seat, vehicleId }
    const [selectedSeats, setSelectedSeats] = useState([]);
    const view = 'oneWay';

    const { seatSelectionEnabled } = useSelector(state => state.userSettings);
    const isSelfSale = tripData.operatorId === getIniLoggedUser()?.id && seatSelectionEnabled

    // preprocesiranje (tvoj postojeći util)
    const busesData = useMemo(() => processBusData(filteredSeats, isSelfSale), [filteredSeats]);

    const availableKey = useMemo(() => {
        const set = new Set();
        busesData.forEach(bus => bus.seats.forEach(seat => {
            if (seat && seat.status === SEAT_STATUS.AVAILABLE) set.add(`${seat.id}-${bus.id}`);
        }));
        return set;
    }, [busesData]);

    // helper: da li je sediste blokirano
    const isSeatBlocked = (seat) =>
        seat?.status === SEAT_STATUS.DISABLED ||
        seat?.status === SEAT_STATUS.RESERVED ||
        seat?.status === SEAT_STATUS.OCCUPIED;

    // vraća status za prikaz BusLayout-u
    const getSeatStatus = (seat, vehicleId) => {
        if (isSeatBlocked(seat)) return seat.status;
        const chosen = selectedSeats.some(s => s.seat === seat.id && s.vehicleId === vehicleId);
        return chosen ? SEAT_STATUS.SELECTED : SEAT_STATUS.AVAILABLE;
    };

    // toggle select
    const handleSelectSeat = (seatId, vehicleId) => {
        // nadji seat objekat da proverimo status
        const seatObj = filteredSeats.find(s => s.seat === seatId && s.vehicleId === vehicleId);
        if (!seatObj) return;

        const status = seatObj.occupied
            ? SEAT_STATUS.OCCUPIED
            : seatObj.reserved
                ? SEAT_STATUS.RESERVED
                : SEAT_STATUS.AVAILABLE;

        if (isSeatBlocked({ id: seatId, status })) return;

        const allotmentId = seatObj.alId ?? seatObj.allotmentId ?? null;

        setSelectedSeats(prev => {
            const exists = prev.some(s => s.seat === seatId && s.vehicleId === vehicleId);
            if (exists) {
                // deselect
                const next = prev.filter(s => !(s.seat === seatId && s.vehicleId === vehicleId));
                onChangeSelectedSeats?.(next);
                return next;
            }
            // select ako ima mesta
            if (prev.length < totalPassengers) {
                const next = [...prev, { seat: seatId, vehicleId ,allotmentId }];
                onChangeSelectedSeats?.(next);
                return next;
            }
            // nema mesta – ignoriši ili (alternativno) zameni poslednji
            return prev;
        });
    };

    // ako se broj putnika smanji, odseci visak izbora
    useEffect(() => {
        setSelectedSeats(prev => {
            const next = prev.slice(0, Math.max(0, totalPassengers));
            if (next !== prev) onChangeSelectedSeats?.(next);
            return next;
        });
    }, [totalPassengers]); // eslint-disable-line react-hooks/exhaustive-deps

    // ako se podaci o sedištima promene, izbaci nevažeća i automatski dodeli slobodna sedišta
    useEffect(() => {
        if (!filteredSeats?.length) return;
        setSelectedSeats(prev => {
            // zadrži samo validna (postojeća i slobodna) prethodno izabrana sedišta
            const valid = prev.filter(sel => {
                const seat = filteredSeats.find(s => s.seat === sel.seat && s.vehicleId === sel.vehicleId);
                if (!seat) return false;
                return !(seat.occupied || seat.reserved);
            }).slice(0, Math.max(0, totalPassengers));

            // automatska dodela: ako nedostaju sedišta, popuni PRVIM koja su u layout-u zaista AVAILABLE
            // (isti kriterijum kao disable logika) — nikad sedište koje korisnik ne može da klikne.
            if (valid.length < totalPassengers) {
                const availableSeats = filteredSeats.filter(s =>
                    availableKey.has(`${s.seat}-${s.vehicleId}`) &&
                    !valid.some(sel => sel.seat === s.seat && sel.vehicleId === s.vehicleId)
                );
                for (const s of availableSeats) {
                    if (valid.length >= totalPassengers) break;
                    valid.push({
                        seat: s.seat,
                        vehicleId: s.vehicleId,
                        allotmentId: s.alId ?? s.allotmentId ?? null
                    });
                }
            }

            onChangeSelectedSeats?.(valid);
            return valid;
        });
    }, [filteredSeats, totalPassengers]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTimerEnd = () => { /* no-op za sada */ };

    if (loading) {
        return <LoadingSegment text={`${translate("loadingSeats")}`}/>;
    }

    return (
        <>
            <Divider sx={{borderStyle: "dashed", mb: 1}}>
                <Chip label={translate("seatsSelection")}/>
            </Divider>

            <AnimatePresence mode="wait">
                <motion.div
                    key={view}
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -20}}
                    transition={{duration: 0.2}}
                >
                    {busesData.map(bus => (
                        <BusLayout
                            key={bus.id}
                            bus={bus}
                            getSeatStatus={(seat, vId) => getSeatStatus(seat, vId)}
                            handleSelectSeat={(sId, vId) => handleSelectSeat(sId, vId)}
                            onTimerEnd={handleTimerEnd}
                        />
                    ))}
                </motion.div>
            </AnimatePresence>

            <Legend />
        </>
    );
}
